import { useState, useEffect, useMemo } from 'react';
import { MainLayout } from '../components/layout/MainLayout';
import { CabecalhoPagina, FaixaFiltros } from '../components/layout/CabecalhoPagina';
import { migrationApi, teamApi, userApi, federationApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, History, ShieldCheck, ArrowRight, Search, Download, Calendar, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import { EstadoVazio } from '../components/common/Estados';

/**
 * Wave 4 — Audit log dashboard.
 * Lists all migration records (athlete/team/professor) with filters.
 * Visible only to super_admin / federation.
 */
export default function AuditPage() {
  const { user } = useAuth();
  const isAllowed = ['super_admin', 'federation'].includes(user?.role);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [professors, setProfessors] = useState([]);
  const [federations, setFederations] = useState([]);

  const [entityFilter, setEntityFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!isAllowed) { setLoading(false); return; }
    Promise.all([
      // Fetch each entity type (backend doesn't allow broad listing without filter for non-admins,
      // but super_admin/federation get everything when no filter given).
      migrationApi.list({ entity_type: 'athlete' }).catch(() => ({ data: [] })),
      migrationApi.list({ entity_type: 'team' }).catch(() => ({ data: [] })),
      migrationApi.list({ entity_type: 'professor' }).catch(() => ({ data: [] })),
      teamApi.list(),
      userApi.list({ role: 'professor' }),
      federationApi.list(),
    ])
      .then(([a, t, p, ts, ps, fs]) => {
        const all = [...(a.data || []), ...(t.data || []), ...(p.data || [])];
        all.sort((x, y) => (y.performed_at || '').localeCompare(x.performed_at || ''));
        setRecords(all);
        setTeams(ts.data || []);
        setProfessors(ps.data || []);
        setFederations(fs.data || []);
      })
      .catch(() => toast.error('Erro ao carregar histórico'))
      .finally(() => setLoading(false));
  }, [isAllowed]);

  const idMap = useMemo(() => {
    const map = {};
    teams.forEach((t) => { map[t._id] = `Equipe: ${t.name}`; });
    professors.forEach((p) => { map[p._id] = `Prof. ${p.name}`; });
    federations.forEach((f) => { map[f._id] = `Fed. ${f.name}`; });
    return map;
  }, [teams, professors, federations]);

  const fieldLabel = (f) => ({
    team_id: 'Equipe',
    professor_id: 'Professor',
    federation_ids: 'Federações',
  }[f] || f);

  const renderValue = (v) => {
    if (v === null || v === undefined || v === '') return <span className="text-[#71717A] italic">vazio</span>;
    /* A unificacao de cadastros duplicados grava `new_value` como OBJETO (ex.:
       {perdedor_id, campos_absorvidos}). Renderizar objeto no JSX derruba o
       React ("Objects are not valid as a React child") e, como a pagina nao tem
       fronteira propria, o app inteiro ficava PRETO. Aqui vira texto. */
    if (typeof v === 'object') return <span title={JSON.stringify(v)}>{JSON.stringify(v)}</span>;
    try {
      const parsed = JSON.parse(v);
      if (Array.isArray(parsed)) {
        return <span>{parsed.map((x) => idMap[x] || x).join(', ') || '—'}</span>;
      }
    } catch (e) { /* not JSON */ }
    return <span>{idMap[v] || v}</span>;
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('pt-BR');
  };

  const filtered = useMemo(() => {
    return records.filter((r) => {
      if (entityFilter !== 'all' && r.entity_type !== entityFilter) return false;
      if (searchTerm) {
        const hay = [
          r.entity_id, r.reason, r.old_value, r.new_value,
          r.performed_by_email, r.performer_role, r.field_changed
        ].join(' ').toLowerCase();
        if (!hay.includes(searchTerm.toLowerCase())) return false;
      }
      return true;
    });
  }, [records, entityFilter, searchTerm]);

  const exportCsv = () => {
    const headers = ['Data', 'Tipo', 'Entidade ID', 'Campo', 'Antigo', 'Novo', 'Motivo', 'Executor', 'Papel'];
    const rows = filtered.map((r) => [
      formatDate(r.performed_at),
      r.entity_type,
      r.entity_id,
      r.field_changed,
      r.old_value || '',
      r.new_value || '',
      (r.reason || '').replace(/[\n,]/g, ' '),
      r.performed_by_email || r.performed_by || '',
      r.performer_role || '',
    ]);
    const csv = [headers, ...rows].map((row) =>
      row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    const blob = new Blob(["\ufeff" + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `auditoria_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (!isAllowed) {
    return (
      <MainLayout>
        <Card className="bg-[#141414] border-[#27272A] max-w-md mx-auto mt-12">
          <CardContent className="p-8 text-center">
            <ShieldCheck className="w-12 h-12 text-[#71717A] mx-auto mb-3" />
            <h2 className="text-xl font-bold text-white mb-2">Acesso restrito</h2>
            <p className="text-[#A1A1AA] text-sm">A página de Auditoria é visível apenas para Super Admin e Federações.</p>
          </CardContent>
        </Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="audit-page">
        <CabecalhoPagina
          contexto="Administração"
          titulo="Auditoria de Migrações"
          descricao="Registro completo de transferências de atletas, professores e mudanças de federação."
          icone={ShieldCheck}
          acoes={
            <Button onClick={exportCsv} variant="outline" data-testid="audit-export" className="border-[#27272A] text-[#A1A1AA] hover:text-white">
              <Download size={16} className="mr-2" /> Exportar CSV
            </Button>
          }
        />

        {/* Filtros em faixa própria (Etapa Visual 2) */}
        <FaixaFiltros rotulo="Buscar e filtrar">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={16} />
            <Input
              placeholder="Buscar por motivo, executor, ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="audit-search"
              className="pl-10 bg-[#141414] border-[#27272A] text-white placeholder:text-[#71717A]"
            />
          </div>
          <div className="w-56">
            <Select value={entityFilter} onValueChange={setEntityFilter}>
              <SelectTrigger data-testid="audit-entity-filter" className="bg-[#141414] border-[#27272A] text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                <SelectItem value="all" className="text-white">Todos os tipos</SelectItem>
                <SelectItem value="athlete" className="text-white">Atletas</SelectItem>
                <SelectItem value="professor" className="text-white">Professores</SelectItem>
                <SelectItem value="team" className="text-white">Equipes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        </FaixaFiltros>

        {/* Records */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span></div>
        ) : filtered.length === 0 ? (
          <Card className="bg-[#141414] border-[#27272A]">
            <CardContent>
              <EstadoVazio icone={History} descricao="Nenhum registro encontrado com esses filtros." />
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-[#141414] border-[#27272A]">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="tabela w-full">
                  <thead>
                    <tr className="border-b border-[#27272A] text-left text-[#71717A] text-xs uppercase tracking-wider">
                      <th className="py-3 px-4">Data</th>
                      <th className="py-3 px-4">Tipo</th>
                      <th className="py-3 px-4">Campo</th>
                      <th className="py-3 px-4">Mudança</th>
                      <th className="py-3 px-4">Motivo</th>
                      <th className="py-3 px-4">Executor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => (
                      <tr key={r._id} className="border-b border-[#27272A]/50 hover:bg-[#1A1A1A] text-sm" data-testid={`audit-row-${r._id}`}>
                        <td className="py-3 px-4 text-white whitespace-nowrap"><Calendar size={12} className="inline mr-1 text-[#71717A]" />{formatDate(r.performed_at)}</td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-0.5 rounded text-xs font-semibold bg-[#1A1A1A] text-[#D4AF37] border border-[#D4AF37]/30 capitalize">{r.entity_type}</span>
                        </td>
                        <td className="py-3 px-4 text-[#A1A1AA]">{fieldLabel(r.field_changed)}</td>
                        <td className="py-3 px-4 text-white">
                          <div className="flex items-center gap-2 flex-wrap">
                            {renderValue(r.old_value)}
                            <ArrowRight size={12} className="text-[#D4AF37] shrink-0" />
                            {renderValue(r.new_value)}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-[#A1A1AA] italic max-w-xs truncate" title={r.reason}>{r.reason || '—'}</td>
                        <td className="py-3 px-4 text-[#A1A1AA] text-xs whitespace-nowrap"><UserIcon size={12} className="inline mr-1" />{r.performed_by_email || r.performed_by || '—'}<br/><span className="text-[#71717A]">{r.performer_role || ''}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        <p className="text-xs text-[#71717A] text-center">
          {filtered.length} registro(s) {entityFilter !== 'all' ? `do tipo ${entityFilter}` : 'no total'} · Histórico imutável conforme LGPD/auditoria.
        </p>
      </div>
    </MainLayout>
  );
}
